/**
 * @module qbLinkage/detectChanges/detectDimensionChanges
 * @description 仅 dimension 类别：增删改重排、dimCount、日期维标记
 */

import type { IQBSnapshot, TQBEvent } from '../types';
import { snapshotList } from '../fieldSnapshot';
import {
  addedFields,
  diffRenames,
  findReorder,
  removedFields,
} from './fieldDiffUtils';
import type { IPartialQBChangeSet } from './partialChangeSet';

function resolveHasDateDimension(snapshot: IQBSnapshot): boolean {
  if (snapshot.hasDateDimension !== undefined) {
    return snapshot.hasDateDimension;
  }
  const dims = snapshotList(snapshot.columnDimensions, 'dimension');
  return dims.some(d => d.column_type === 'date');
}

export function detectDimensionChanges(
  prev: IQBSnapshot,
  next: IQBSnapshot,
): IPartialQBChangeSet {
  const events: TQBEvent[] = [];
  const prevDims = snapshotList(prev.columnDimensions, 'dimension');
  const nextDims = snapshotList(next.columnDimensions, 'dimension');

  const addedDimensions = addedFields(prevDims, nextDims);
  const removedDimensions = removedFields(prevDims, nextDims);

  if (addedDimensions.length) {
    events.push('dimensionAdded');
  }
  if (removedDimensions.length) {
    events.push('dimensionRemoved');
  }

  const reorderedDimensions = findReorder(prevDims, nextDims);
  if (reorderedDimensions) {
    events.push('dimensionReordered');
  }

  const dimRenamed = diffRenames(prevDims, nextDims);
  const renamed = [...dimRenamed];
  for (const r of dimRenamed) {
    if (r.displayOnly) {
      events.push('fieldRenamed');
    } else {
      events.push('fieldIdentityRenamed');
    }
  }

  const dimCount = nextDims.length;
  if (dimCount !== prevDims.length) {
    events.push('dimCountChanged');
  }

  const hasDateDimension = resolveHasDateDimension(next);
  if (hasDateDimension !== resolveHasDateDimension(prev)) {
    events.push('hasDateDimChanged');
  }

  return {
    events,
    addedDimensions,
    removedDimensions,
    reorderedDimensions,
    renamed,
    dimCount,
    hasDateDimension,
  };
}
